/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { LegacyTimelineDataStore } from "../loggedInUser/LegacyTimelineDataStore";
import { when, observable, runInAction, makeObservable } from "mobx";
import { inject, injectable } from "../injection";
import { windowAsAny } from "../windowAsAny/windowAsAny";

function loadScript(src: string) {
    return new Promise<void>((resolve, _) => {
        const head = document.getElementsByTagName("head")[0];
        const script = document.createElement("script");
        script.src = src;
        script.async = false;
        script.onload = () => {
            resolve();
        };
        head?.appendChild(script);
    });
}

function loadStyle(src: string): void {
    const head = document.getElementsByTagName("head")[0];
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.type = "text/css";
    link.href = src;
    head?.appendChild(link);
}

const locationAppend = () =>
    windowAsAny().redDeerVersionHash
        ? // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `?${windowAsAny().redDeerVersionHash}`
        : "";

const bundles = ["core", "thirdParty", "panel", "shared", "angular"];
const templates = [
    "/Template/UI",
    "/Template/PanelSystem",
    "/Template/UI.Pages",
    "/Template/UI.Controls",
    "/Template/Common.Web"
];

@injectable("singleton")
export class AngularLoaderStore {
    @observable public hasLoaded = false;
    private loadInstance: Promise<void> | null = null;

    constructor(
        @inject(LegacyTimelineDataStore)
        private legacyTimelineDataStore: LegacyTimelineDataStore
    ) {
        makeObservable(this);

        // if loading from main reddeer app
        if (!windowAsAny().isRedDeerReactFirst) {
            runInAction(() => {
                this.hasLoaded = true;
            });
            return;
        }
    }

    public loader(): Promise<void> {
        this.loadInstance ??= this.load();
        return this.loadInstance;
    }

    private async load() {
        const w = windowAsAny();

        // if already loaded
        if (this.hasLoaded) {
            return;
        }

        if (w.isRedDeerModuleLoader) {
            // angular scripts included in html (although may not be quite ready)
            const wait = new Promise<void>(resolve => {
                const check = () => {
                    if (w.redDeerAngularHasLoaded) {
                        resolve();
                    } else {
                        setTimeout(check); // allow angular scripts to execute before continuing
                    }
                };
                check();
            });
            await wait;
        } else {
            // load scripts if not already loaded
            w.isRedDeerModuleLoader = true;

            loadStyle(`/css-guide/css/styles.min.css${locationAppend()}`);

            let urls: string[];

            if (w.hasRedDeerBundledScripts) {
                urls = bundles.map(
                    x => `/css-guide/bundledjs/${x}.scripts.min.js`
                );
            } else {
                const responses = await Promise.all(
                    bundles.map(x =>
                        fetch(`/css-guide/bundledjs/${x}.list.html`)
                    )
                );
                const texts = await Promise.all(responses.map(x => x.text()));

                urls = texts
                    .join("")
                    .split(`<script src="`)
                    .map(x => {
                        const ends = x.split(`"></script>`);
                        return ends[0] ?? "";
                    })
                    .filter(x => x !== "");
            }

            urls = urls.concat(templates);
            urls = urls.map(x => `${x}${locationAppend()}`);

            const last = urls.pop();
            urls.forEach(x => {
                void loadScript(x);
            });
            if (last) {
                await loadScript(last);
            }
        }

        // app variables
        w.RedDeer.UI.App.instance.value("$state", {});
        w.RedDeer.UI.App.instance.value("$stateParams", {});
        w.RDPanelLayout.app = {
            userDetails: {
                Variables: {}
            },
            panelManager: {
                pauseUntilCallback: () => {
                    /* do nothing */
                },
                addOneTimeUnPauseEvent: (func: () => void) => {
                    func();
                }
            }
        };

        w.RDPanelLayout.Data.WithCoreData = (
            callback: (
                response: RedDeer.Contracts.Queries.Timeline.GetTimelineDataResponse
            ) => void,
            _: string,
            finallyCallback?: () => void
        ) => {
            void when(() => this.legacyTimelineDataStore.isReady)
                .then(() => {
                    if (this.legacyTimelineDataStore.response) {
                        callback(this.legacyTimelineDataStore.response);
                    }
                })
                .then(() => {
                    finallyCallback?.();
                });
        };

        // set legacy user details data
        await when(() => this.legacyTimelineDataStore.isReady);
        w.RDPanelLayout.app.userDetails =
            this.legacyTimelineDataStore.response?.UserDetails;

        // bootstrap
        const angularReadyPromise = new Promise<void>(resolve => {
            w.RedDeer.UI.App.instance.run(() => {
                resolve();
            });
        });
        w.angular.bootstrap(document, ["angularApp"], {
            strictDi: true
        });

        await angularReadyPromise;

        // set legacy user variables (used in my profile page)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        w.Red.WithUserVariables = (callback: (legacyControl: any) => void) => {
            const legacyControl = w.RedDeer.UI.App.getInject(
                "userVariableService"
            ).legacyControl();
            callback(legacyControl);
        };

        // initialize split.io
        await w.RedDeer.UI.App.getInject("featureSwitchService").initialise();

        runInAction(() => {
            this.hasLoaded = true;
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public async loadService(name: string): Promise<any> {
        await this.loader();
        return windowAsAny().RedDeer.UI.App.getInject(name);
    }
}

export function preloadableScriptUrls(): string[] {
    let urls: string[] = [];
    if (windowAsAny().hasRedDeerBundledScripts) {
        urls = urls.concat(
            bundles.map(x => `/css-guide/bundledjs/${x}.scripts.min.js`)
        );
    }
    urls = urls.concat(templates);
    urls = urls.map(x => `${x}${locationAppend()}`);
    return urls;
}

export function preloadableStyleUrls(): string[] {
    return [`/css-guide/css/styles.min.css${locationAppend()}`];
}
